import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as BioentityDAO from 'bioentities/BioentityDAO';
import {ChemicalFormatType, EMPTY_MOLFILE} from 'chemistry/ChemistryConstants';
import {ChemicalStructureDrawerValue} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {TagSchema} from 'constants/RegistryConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import {SchemaTag} from 'folder-items/FileConstants';
import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {
  CreateBioentityArgs,
  getDefaultCreateBioentityArgs,
  prepareForCreateBioentityServerArgs,
} from 'registry/create-bioentity/CreateBioentityUtil';
import {getLinksKeyFromFieldType, isNullValue, relevantKeyFromFieldType} from 'schemas/tags/TagUtil';
import {StudySearchItem} from 'studies/StudySearchInput';
import {Optional} from 'util/Types';

import * as MoleculeDAO from './MoleculeDAO';
import {stripSaltsAndNotify} from './MoleculeUtil';

export interface CreateMoleculeArgs {
  name: string;
  folderId: string | null;
  sketcherValue: ChemicalStructureDrawerValue;
  tagSchema: Optional<TagSchema>;
  tags: Array<SchemaTag>;
  studySearchItems: Array<StudySearchItem>;
  registerAfterCreation: boolean;
  namingStrategy: RegistryLabelingStrategy | null;
}

export interface CreateMoleculeResult {
  id: string;
  name: string;
  parentMolecule?: MoleculeDAO.CreatedParentMolecule;
}

export async function createMolecule({
  name,
  folderId,
  sketcherValue,
  tagSchema,
  tags,
  studySearchItems,
  registerAfterCreation,
  namingStrategy,
}: CreateMoleculeArgs): Promise<CreateMoleculeResult> {
  const chemicalStructure = sketcherValue ?? EMPTY_MOLFILE;
  const studyIds = studySearchItems.map(({id}) => id);
  const shouldStripSalts = !BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS && sketcherValue != null;

  let result: CreateMoleculeResult;

  if (BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS) {
    assert(tagSchema != null, 'Schema is required when salt form bulk create is enabled');
    const createParams: MoleculeDAO.CreateMoleculeWithParentLinkParams = {
      name,
      schemaId: tagSchema.id,
      chemicalStructureInput: {
        structureFormat: ChemicalFormatType.MOLFILE,
        value: chemicalStructure,
      },
      schemaFields: buildSchemaFieldsFromTags(tags),
      studyIds,
    };
    if (folderId != null) {
      createParams.folderId = folderId;
    }
    if (registerAfterCreation) {
      assert(
        tagSchema.registryId != null,
        'registryId is required when registering a molecule after creation'
      );
      assert(namingStrategy != null, 'namingStrategy is required when registering a molecule after creation');
      createParams.registryId = tagSchema.registryId;
      createParams.namingStrategy = namingStrategy;
    }
    const molecule = await MoleculeDAO.createMoleculeWithParentLink(createParams);
    writeFragmentsToCache(getFragmentWrapper('Molecule'), [molecule]);
    result = {
      id: molecule.id,
      name: molecule.name,
      parentMolecule: molecule.parentMolecule,
    };
  } else if (tagSchema != null) {
    const createBioentityArgs: CreateBioentityArgs = {
      ...getDefaultCreateBioentityArgs(),
      name,
      folderId,
      tagSchema,
      tags,
      studySearchItems,
      chemicalSketcherValue: sketcherValue,
    };
    const bioentity = await BioentityDAO.create(prepareForCreateBioentityServerArgs(createBioentityArgs));
    result = {id: bioentity.id, name: bioentity.name};
  } else {
    assert(folderId != null, 'folderId is required when creating a plain molecule');
    const molecule = await MoleculeDAO.createMolecule(
      name,
      folderId,
      ChemicalFormatType.MOLFILE,
      chemicalStructure,
      null,
      studyIds
    );
    writeFragmentsToCache(getFragmentWrapper('Molecule'), [molecule]);
    result = {id: molecule.id, name: molecule.name};
  }

  if (shouldStripSalts) {
    await stripSaltsAndNotify(ChemicalFormatType.MOLFILE, sketcherValue);
  }

  if (folderId != null) {
    FolderDAOCache.invalidateFolderContents(folderId);
  }
  return result;
}

/**
 * Converts schema tags to the `schemaFields` payload for molecules:create-with-parent-link.
 * Keys are field system names; empty values are omitted. Link and selector fields are
 * normalized to the single ID or ID array shape the backend expects.
 */
export function buildSchemaFieldsFromTags(tags: Array<SchemaTag>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const tag of tags) {
    const {systemName, isMulti, fieldType} = tag.tagSchemaField;

    const key = relevantKeyFromFieldType(fieldType);
    if (!key) {
      continue;
    }

    const value = tag[key];
    if (isNullValue(value)) {
      continue;
    }

    // Handle link fields (bioentity, batch, entry, etc.) - map objects to IDs.
    // Like selector fields, the backend expects a single string ID for single-link
    // fields and an array of string IDs for multi-link fields. The frontend always
    // stores the targets as an array.
    if (getLinksKeyFromFieldType(fieldType) != null && Array.isArray(value)) {
      const ids = (value as Array<{id: string}>).map((item) => item.id);
      result[systemName] = isMulti ? ids : ids[0];
    } else if (key === 'schemaFieldSelectorOptionIds' && Array.isArray(value)) {
      // Handle selector/dropdown fields
      // For single-select, the backend expects a single string ID, not an array
      // For multi-select, the backend expects an array of string IDs
      // The frontend always stores schemaFieldSelectorOptionIds as an array
      result[systemName] = isMulti ? value : value[0];
    } else {
      result[systemName] = value;
    }
  }
  return result;
}
